import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  Languages, Upload, Loader2, Download, Copy, FileIcon, Clock, Globe, ArrowLeftRight
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

import { TRANSLATE_LANGUAGES } from "@/config/languages";

const ALL_LANGUAGES = ["Auto-detect", ...TRANSLATE_LANGUAGES];

export default function Translate() {
  const [translationMode, setTranslationMode] = useState("global");
  const [translateContent, setTranslateContent] = useState("");
  const [translateLang, setTranslateLang] = useState("Auto-detect");
  const [translateTargetLang, setTranslateTargetLang] = useState("English");
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedText, setTranslatedText] = useState("");
  const [translateFile, setTranslateFile] = useState(null);
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);
  const [docTranslations, setDocTranslations] = useState([]);

  const isRsa = translationMode === "rsa";

  const sourceLanguages = isRsa ? ALL_LANGUAGES : ALL_LANGUAGES;
  const targetLanguages = (isRsa ? ALL_LANGUAGES : ALL_LANGUAGES).filter(l => l !== "Auto-detect");

  useEffect(() => {
    fetchDocTranslations();
  }, []);

  // Reset language selections when toggling to African Languages mode
  useEffect(() => {
    if (isRsa) {
      if (!ALL_LANGUAGES.includes(translateLang)) {
        setTranslateLang("Auto-detect");
      }
      if (!ALL_LANGUAGES.includes(translateTargetLang)) {
        setTranslateTargetLang("English");
      }
    }
  }, [translationMode]);

  const fetchDocTranslations = async () => {
    try {
      const res = await axios.get(`${API}/api/media/translations`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setDocTranslations(res.data);
    } catch (err) {
      // No translations yet
    }
  };

  const handleTranslate = async () => {
    if (!translateContent.trim()) {
      toast.error("Please enter content to translate");
      return;
    }

    setIsTranslating(true);
    setTranslatedText("");

    try {
      const endpoint = isRsa ? `${API}/api/media/rsa/translate` : `${API}/api/media/translate`;
      const res = await axios.post(endpoint, {
        content: translateContent,
        source_language: translateLang,
        target_language: translateTargetLang
      }, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });

      setTranslatedText(res.data.translated);
      toast.success("Translation complete");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Translation failed");
    }
    setIsTranslating(false);
  };

  const handleDocumentTranslate = async () => {
    if (!translateFile) {
      toast.error("Please select a document to translate");
      return;
    }

    setIsUploadingDoc(true);
    setTranslatedText("");

    try {
      const endpoint = isRsa ? `${API}/api/media/rsa/translate-document` : `${API}/api/media/translate-document`;
      const formData = new FormData();
      formData.append("file", translateFile);
      formData.append("source_language", translateLang);
      formData.append("target_language", translateTargetLang);

      const res = await axios.post(endpoint, formData, {
        headers: getHeaders(),
        withCredentials: true
      });

      setTranslatedText(res.data.translation);
      toast.success(`Document translated (${res.data.character_count} characters)`);
      setTranslateFile(null);
      fetchDocTranslations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Document translation failed");
    }
    setIsUploadingDoc(false);
  };

  const handleDownload = (text, filename = "translation.txt") => {
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Translation downloaded");
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
            <Languages className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800 tracking-wide">
              {isRsa ? "ALL LANGUAGES" : "GLOBAL TRANSLATIONS"}
            </h1>
            <p className="text-slate-600">Translate documents and text between languages</p>
          </div>
        </div>

        {/* Mode Toggle */}
        <div className="flex gap-2 mt-4 bg-slate-100 p-1 inline-flex rounded-lg">
          <button
            onClick={() => setTranslationMode("global")}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              !isRsa
                ? "bg-white text-violet-700 shadow-sm"
                : "text-slate-600 hover:text-slate-800"
            }`}
          >
            <Globe className="w-4 h-4" />
            Global Translations
          </button>
          <button
            onClick={() => setTranslationMode("rsa")}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              isRsa
                ? "bg-white text-violet-700 shadow-sm"
                : "text-slate-600 hover:text-slate-800"
            }`}
          >
            <Languages className="w-4 h-4" />
            African Languages
          </button>
        </div>
        {isRsa && (
          <p className="text-xs text-amber-600 mt-2">
            African Languages powered by Qwen 3.5 — covers 40+ African languages
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Source Content */}
        <div className="space-y-6">
          {/* Document Upload */}
          <div className="bg-white border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5 text-violet-500" />
              Document Upload
            </h3>
            <p className="text-sm text-slate-600 mb-4">
              Upload PDF, DOCX, or TXT files for translation
            </p>

            <div className="flex items-end gap-2 mb-4">
              <div className="flex-1">
                <label className="text-xs text-slate-500 mb-1 block">Source Language</label>
                <Select value={translateLang} onValueChange={setTranslateLang}>
                  <SelectTrigger className="bg-app border-slate-300 rounded-none" data-testid="translate-source-lang">
                    <SelectValue placeholder="Source language" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-300 rounded-none">
                    {sourceLanguages.map(lang => (
                      <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (translateLang !== "Auto-detect") {
                    const prev = translateLang;
                    setTranslateLang(translateTargetLang);
                    setTranslateTargetLang(prev);
                  }
                }}
                className={`mb-0.5 p-2 rounded-md transition-colors ${
                  translateLang === "Auto-detect"
                    ? "text-slate-300 cursor-not-allowed"
                    : "text-violet-500 hover:bg-violet-100 hover:text-violet-700"
                }`}
                title="Swap languages"
                disabled={translateLang === "Auto-detect"}
              >
                <ArrowLeftRight className="w-4 h-4" />
              </button>
              <div className="flex-1">
                <label className="text-xs text-slate-500 mb-1 block">Target Language</label>
                <Select value={translateTargetLang} onValueChange={setTranslateTargetLang}>
                  <SelectTrigger className="bg-app border-slate-300 rounded-none">
                    <SelectValue placeholder="Target language" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-300 rounded-none">
                    {targetLanguages.map(lang => (
                      <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-2 border-dashed border-slate-300 p-6 text-center hover:border-violet-400 transition-colors">
              <input
                type="file"
                accept=".pdf,.docx,.doc,.txt"
                onChange={(e) => setTranslateFile(e.target.files[0])}
                className="hidden"
                id="translate-doc-upload"
                data-testid="translate-doc-input"
              />
              <label htmlFor="translate-doc-upload" className="cursor-pointer">
                <FileIcon className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                {translateFile ? (
                  <p className="text-violet-500 font-medium">{translateFile.name}</p>
                ) : (
                  <>
                    <p className="text-slate-600 font-medium">Drop document here or click to browse</p>
                    <p className="text-xs text-slate-500 mt-1">PDF, DOCX, TXT (Max 15MB)</p>
                  </>
                )}
              </label>
            </div>

            <Button
              onClick={handleDocumentTranslate}
              disabled={isUploadingDoc || !translateFile}
              className="w-full mt-4 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 rounded-none"
              data-testid="translate-doc-btn"
            >
              {isUploadingDoc ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Translating Document...
                </>
              ) : (
                <>
                  <Languages className="w-4 h-4 mr-2" />
                  Translate Document
                </>
              )}
            </Button>
          </div>

          {/* Text Input */}
          <div className="bg-white border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-800 mb-4">Text Input</h3>
            <Textarea
              placeholder="Or paste content to translate..."
              value={translateContent}
              onChange={(e) => setTranslateContent(e.target.value)}
              className="bg-app border-slate-300 rounded-none min-h-[200px]"
              data-testid="translate-content-input"
            />
            <Button
              onClick={handleTranslate}
              disabled={isTranslating || !translateContent.trim()}
              className="w-full mt-4 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 rounded-none"
              data-testid="translate-btn"
            >
              {isTranslating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Translating...
                </>
              ) : (
                <>
                  <Languages className="w-4 h-4 mr-2" />
                  Translate Text
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Translation Output */}
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-slate-800">
                {translateTargetLang} Translation
              </h3>
              {translatedText && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-none"
                    onClick={() => {
                      navigator.clipboard.writeText(translatedText);
                      toast.success("Translation copied to clipboard");
                    }}
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-none"
                    onClick={() => handleDownload(translatedText, "translation.txt")}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Download
                  </Button>
                </div>
              )}
            </div>

            {translatedText ? (
              <ScrollArea className="h-[400px] border border-slate-200 p-4 bg-app">
                <pre className="whitespace-pre-wrap text-slate-700 font-['IBM_Plex_Sans'] text-sm leading-relaxed">
                  {translatedText}
                </pre>
              </ScrollArea>
            ) : (
              <div className="h-[400px] flex items-center justify-center border border-dashed border-slate-300">
                <div className="text-center">
                  <Languages className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Translation will appear here</p>
                </div>
              </div>
            )}
          </div>

          {/* Recent Translations */}
          {docTranslations.length > 0 && (
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-800 mb-4">Recent Translations</h3>
              <div className="space-y-2">
                {docTranslations.slice(0, 5).map((trans) => (
                  <div
                    key={trans.id}
                    className="p-3 bg-app border border-slate-200 hover:border-violet-400 transition-colors"
                  >
                    <div
                      className="cursor-pointer"
                      onClick={() => setTranslatedText(trans.translated_text)}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <FileIcon className="w-4 h-4 text-violet-500" />
                        <span className="text-sm font-medium text-slate-700 truncate">
                          {trans.original_filename}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span>{trans.source_language} → {trans.target_language || "English"}</span>
                        <span>•</span>
                        <Clock className="w-3 h-3" />
                        <span>{new Date(trans.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2 h-7 text-xs rounded-none text-violet-600 hover:text-violet-800"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(
                          trans.translated_text,
                          `${trans.original_filename?.replace(/\.[^.]+$/, "") || "translation"}_translated.txt`
                        );
                      }}
                    >
                      <Download className="w-3 h-3 mr-1" />
                      Download
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
